import * as React from "react";

import Avatar from "@editor/components/common/designSystem/Avatar";
import Input from "@editor/components/common/designSystem/Input";
import { SidebarWorkspacesSkeleton } from "@editor/components/dashboard/SkeletonLoaders";
import { WorkspaceCardSkeleton } from "@editor/components/editor/SkeletonLoaders";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useSubscriptionInfo } from "@editor/hooks/subscription";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { getProjectName } from "@editor/utils/project-utils";
import { routes } from "@editor/utils/router";
import { trpc } from "@editor/utils/trpc";

import IconButton from "@replo/design-system/components/button/IconButton";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import Popover from "@replo/design-system/components/popover/Popover";
import { Tag } from "@replo/design-system/components/tag/Tag";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useOverflowDetection } from "@replo/design-system/utils/useOverflowDetection";
import { skipToken } from "@tanstack/react-query";
import { ChevronsUpDown, Plus, Search, Settings } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

type CardProps = {
  workspaceName: string;
  endEnhancer?: React.ReactNode;
  className?: string;
  disableConditionalStyles?: boolean;
  isEmptyWorkspaces?: boolean;
  isPopoverOpen?: boolean;
  shopName?: string;
  isSidebarExpanded?: boolean;
};

const ShopSelector: React.FC<{
  isSidebarExpanded: boolean;
  onPopoverOpenChange?: (isOpen: boolean) => void;
}> = ({
  isSidebarExpanded,
  onPopoverOpenChange: onPopoverOpenChangeCallback,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [searchStringInputValue, searchString, setSearchString] =
    useDebouncedState("");
  const logEvent = useLogAnalytics();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const shopId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const { data, isLoading } =
    trpc.project.listGroupedByWorkspaceForUser.useQuery(user?.id ?? skipToken);
  const workspaceId = useCurrentWorkspaceId();
  const currentWorkspaceName = React.useMemo(
    () =>
      data?.find((workspace) => workspace.workspaceId === workspaceId)
        ?.workspaceName,
    [workspaceId, data],
  );
  const { project } = useCurrentProjectContext();
  const isAllShopsView = location.pathname.startsWith(routes.allProjects);
  const currentShopName = (() => {
    if (project) {
      return getProjectName(project);
    }
    if (currentWorkspaceName) {
      return currentWorkspaceName;
    }
    if (isAllShopsView) {
      return "All Shops";
    }
    return "Shop";
  })();

  const onShopClick = (shopId: string, shopName: string | undefined | null) => {
    navigate(`/shops/${shopId}/site`);
    setIsPopoverOpen(false);

    logEvent("dashboard.shop.select", {
      toShopName: shopName ?? "",
      toShopId: shopId,
    });
  };

  const onPopoverOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen);
    setSearchString("");
    onPopoverOpenChangeCallback?.(isOpen);
  };

  // REPL-17711: Handle Enter key to select first shop when search has content
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchString.trim() !== "") {
      // Find the first shop in the filtered data
      const firstWorkspaceWithShops = filteredData.find(
        (workspace) => workspace.projects.length > 0,
      );

      if (
        firstWorkspaceWithShops &&
        firstWorkspaceWithShops.projects.length > 0
      ) {
        const firstShop = firstWorkspaceWithShops.projects[0]!;
        onShopClick(firstShop.id, getProjectName(firstShop));
      }
    }
  };

  const filteredData = React.useMemo(() => {
    if (!data) {
      return [];
    }

    let result: typeof data;

    if (!searchString.trim()) {
      result = data;
    } else {
      const lowercasedSearchString = searchString.toLowerCase();
      result = data
        .map((workspaceOrCollaboratorGroup) => {
          const filteredProjects = workspaceOrCollaboratorGroup.projects.filter(
            (project) =>
              project.name?.toLowerCase().includes(lowercasedSearchString),
          );
          const workspaceNameMatches =
            workspaceOrCollaboratorGroup.workspaceName
              ?.toLowerCase()
              .includes(lowercasedSearchString);
          if (workspaceNameMatches || filteredProjects.length > 0) {
            return {
              ...workspaceOrCollaboratorGroup,
              projects:
                workspaceNameMatches && filteredProjects.length === 0
                  ? workspaceOrCollaboratorGroup.projects
                  : filteredProjects,
            };
          }
          return null;
        })
        .filter(
          (workspaceOrCollaboratorGroup) =>
            workspaceOrCollaboratorGroup !== null,
        ) as typeof data;
    }

    // Sort workspaces: current workspace first, then alphabetically
    return result.sort((a, b) => {
      // Current workspace should appear first
      if (a.workspaceId === workspaceId) {
        return -1;
      }
      if (b.workspaceId === workspaceId) {
        return 1;
      }

      // Sort remaining workspaces alphabetically by name
      const nameA = a.workspaceName?.toLowerCase() ?? "";
      const nameB = b.workspaceName?.toLowerCase() ?? "";
      return nameA.localeCompare(nameB);
    });
  }, [data, searchString, workspaceId]);

  return (
    <Popover.Root isOpen={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
      <Popover.Content
        shouldPreventDefaultOnInteractOutside={false}
        side={isSidebarExpanded ? "bottom" : "right"}
        sideOffset={isSidebarExpanded ? 4 : 16}
        alignOffset={isSidebarExpanded ? 4 : -12}
        hideCloseButton
        align="start"
        className="w-[200px] p-0"
      >
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div className="border-b-0.5 border-border flex flex-col gap-2 p-3">
              <Input
                size="base"
                placeholder="Search"
                layoutClassName="grow"
                type="text"
                value={searchStringInputValue}
                onChange={(e) => setSearchString(e.target.value ?? "")}
                onKeyDown={handleSearchKeyDown}
                startEnhancer={<Search className="text-muted" size={16} />}
              />
            </div>
            <div className="py-1.5">
              <div
                className="flex max-h-[270px] flex-col gap-2 px-3 py-1.5 styled-scrollbar overflow-y-scroll pr-0 box-content"
                aria-label="workspaces"
              >
                {!isLoading &&
                  filteredData?.map((workspaceOrCollaboratorGroup) => (
                    <div
                      key={workspaceOrCollaboratorGroup.workspaceId}
                      className="flex flex-col gap-1.5"
                    >
                      <div className="flex flex-row">
                        <div className="header-h5 flex min-h-6 items-center pl-2 grow">
                          {workspaceOrCollaboratorGroup.workspaceId
                            ? workspaceOrCollaboratorGroup.workspaceName
                            : "Collaborator Shops"}
                        </div>
                        {workspaceOrCollaboratorGroup.workspaceId && (
                          <IconButton
                            variant="tertiary"
                            size="sm"
                            tooltipText="Workspace Settings"
                            icon={<Settings size={12} className="text-muted" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/workspace/${workspaceOrCollaboratorGroup.workspaceId}/settings`,
                              );
                            }}
                          />
                        )}
                      </div>
                      {workspaceOrCollaboratorGroup.currentUserRole ===
                        "workspaceMember" && (
                        <MenuItem
                          key={`new-shop-${workspaceOrCollaboratorGroup.workspaceId}`}
                          variant="default"
                          size="base"
                          onClick={() =>
                            openModal({
                              type: "createShopFlowModal",
                              props: {
                                workspaceId:
                                  workspaceOrCollaboratorGroup.workspaceId ??
                                  "",
                              },
                            })
                          }
                          startEnhancer={
                            <div className="border-border-component bg-sheet flex h-5 w-5 items-center justify-center rounded-[5px] border border-dashed">
                              <Plus
                                size={12}
                                className="text-muted"
                                absoluteStrokeWidth
                                strokeWidth={1.25}
                              />
                            </div>
                          }
                        >
                          New Shop
                        </MenuItem>
                      )}
                      {workspaceOrCollaboratorGroup.projects.map((project) => (
                        <MenuItem
                          key={project.id}
                          variant="default"
                          size="base"
                          selected={project.id === shopId}
                          onClick={() =>
                            onShopClick(project.id, getProjectName(project))
                          }
                          startEnhancer={
                            <Avatar
                              name={getProjectName(project)}
                              size="xs"
                              className="rounded-md"
                              imageClassName={twMerge(
                                "bg-info-soft text-primary text-[10px]",
                                project.id === shopId && "bg-white",
                              )}
                            />
                          }
                        >
                          {getProjectName(project)}
                        </MenuItem>
                      ))}
                    </div>
                  ))}
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <SidebarWorkspacesSkeleton key={i.toString()} />
                  ))}
                {!isLoading &&
                  filteredData?.length === 0 &&
                  searchString.trim() !== "" && (
                    <div className="text-muted p-2 text-center text-sm">
                      No results found.
                    </div>
                  )}
              </div>
            </div>
          </div>
          <div className="text-primary border-t-0.5 border-border flex p-1.5">
            <button
              className="hover:bg-button-link-hover flex h-6.5 w-full items-center gap-2 rounded-sm pl-3.5 text-sm"
              onClick={() => openModal({ type: "addWorkspaceModal" })}
            >
              <div className="flex h-5 w-5 items-center justify-center">
                <Plus size={14} />
              </div>
              New Workspace
            </button>
          </div>
        </div>
      </Popover.Content>
      <Popover.Trigger className="w-full" aria-label="select workspace">
        {isLoading ? (
          <WorkspaceCardSkeleton compact={!isSidebarExpanded} />
        ) : (
          <Card
            workspaceName={
              isAllShopsView ? "Member or shared" : currentWorkspaceName ?? ""
            }
            shopName={currentShopName}
            endEnhancer={<ChevronsUpDown size={16} className="text-default" />}
            isPopoverOpen={isPopoverOpen}
            isSidebarExpanded={isSidebarExpanded}
          />
        )}
      </Popover.Trigger>
    </Popover.Root>
  );
};

const Card: React.FC<CardProps> = ({
  workspaceName,
  shopName,
  endEnhancer,
  className,
  disableConditionalStyles,
  isPopoverOpen,
  isSidebarExpanded,
}) => {
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();
  const { userRole } = useCurrentProjectContext();
  const location = useLocation();
  const [shopOverflowRef, isShopOverflowing] = useOverflowDetection();
  const [workspaceOverflowRef, isWorkspaceOverflowing] = useOverflowDetection();
  const {
    subscriptionInfo: { tier },
    isLoading,
  } = useSubscriptionInfo(workspaceId ?? undefined);

  const isAllShopsView = location.pathname.startsWith(routes.allProjects);

  if (!user) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-row gap-2 rounded-md p-1 h-10 relative items-center",
        !disableConditionalStyles && "hover:bg-menu-item-hover cursor-pointer",
        isPopoverOpen && "bg-menu-item-hover",
        !isSidebarExpanded && "hover:bg-transparent",
        isPopoverOpen && !isSidebarExpanded && "bg-transparent",
        className,
      )}
    >
      <Avatar
        name={shopName}
        isSquare
        size="base"
        className="py-0.5 rounded-md items-center w-8"
        imageClassName="bg-info-soft text-primary border-0.5 border-menu-item-selected w-8 h-8"
      />
      {isSidebarExpanded && (
        <>
          <div className="flex w-full flex-col text-left h-9">
            <div className="text-default inline-grid w-full text-base font-semibold">
              {isShopOverflowing ? (
                <Tooltip content={shopName} side="right" triggerAsChild>
                  <span
                    ref={shopOverflowRef}
                    className="truncate max-w-[135px] min-w-0"
                  >
                    {shopName}
                  </span>
                </Tooltip>
              ) : (
                <span
                  ref={shopOverflowRef}
                  className="truncate max-w-[135px] min-w-0"
                >
                  {shopName}
                </span>
              )}
            </div>
            <div className="flex gap-1 items-center text-muted text-sm">
              {isWorkspaceOverflowing ? (
                <Tooltip content={workspaceName} side="right" triggerAsChild>
                  <span
                    ref={workspaceOverflowRef}
                    className={twMerge(
                      "truncate min-w-0",
                      !isAllShopsView && "max-w-[100px]",
                    )}
                  >
                    {workspaceName}
                  </span>
                </Tooltip>
              ) : (
                <span
                  ref={workspaceOverflowRef}
                  className={twMerge(
                    "truncate min-w-0",
                    !isAllShopsView && "max-w-[100px]",
                  )}
                >
                  {workspaceName}
                </span>
              )}
              {!isLoading &&
                userRole !== "projectMember" &&
                !isAllShopsView && (
                  <Tag variant="success" size="sm">
                    <span className="capitalize">{tier}</span>
                  </Tag>
                )}
            </div>
          </div>
          <div className="shrink-0 absolute right-0.5 top-0.5">
            {endEnhancer}
          </div>
        </>
      )}
    </div>
  );
};

export default ShopSelector;
